// Helpers tipados del MÓDULO 6 (Notificaciones y Comunicaciones).
// Centraliza las llamadas al backend para que las vistas no repitan rutas/DTOs.
// Base de la API y token JWT los resuelve `api` (lib/api.ts). Todo requiere Bearer.

import { api } from './api';

// ---- Constantes locales (enums del backend) -------------------------------

export const TIPO_NOTIFICACION = [
  'PERSONAL',
  'ELECTRONICA',
  'ESTADO',
  'EDICTO',
  'ESTRADOS',
  'COMUNICACION',
] as const;
export type TipoNotificacion = (typeof TIPO_NOTIFICACION)[number];

export const ESTADO_NOTIFICACION = [
  'PENDIENTE',
  'ENVIADA',
  'ENTREGADA',
  'DEVUELTA',
  'FALLIDA',
] as const;
export type EstadoNotificacion = (typeof ESTADO_NOTIFICACION)[number];

// Etiquetas legibles en español para los enums.
export const TIPO_LABEL: Record<TipoNotificacion, string> = {
  PERSONAL: 'Personal',
  ELECTRONICA: 'Electrónica',
  ESTADO: 'Por estado',
  EDICTO: 'Edicto',
  ESTRADOS: 'En estrados',
  COMUNICACION: 'Comunicación',
};

export const ESTADO_LABEL: Record<EstadoNotificacion, string> = {
  PENDIENTE: 'Pendiente',
  ENVIADA: 'Enviada',
  ENTREGADA: 'Entregada',
  DEVUELTA: 'Devuelta',
  FALLIDA: 'Fallida',
};

// ---- Tipos del dominio ----------------------------------------------------

export interface Notificacion {
  id: string;
  tipo: string;
  estado: string;
  destinatario?: string | null;
  correo?: string | null;
  asunto?: string | null;
  fecha_envio?: string | null;
  fecha_notificacion?: string | null;
  // El radicado puede venir directo o anidado en `expediente`.
  radicado?: string | null;
  expediente?: { radicado?: string | null } | null;
  [k: string]: unknown;
}

export interface PaginaNotif<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

/** Resuelve el radicado, venga plano o anidado en `expediente`. */
export function radicadoDe(n: Notificacion): string {
  return n.radicado || n.expediente?.radicado || '—';
}

// ---- DTOs de entrada (camelCase, como espera el backend) ------------------

export interface CrearNotificacionInput {
  tipo: TipoNotificacion;
  actuacionId?: string;
  destinatario?: string;
  calidadDestinatario?: string;
  correo?: string;
  asunto?: string;
  fechaEnvio?: string;
  fechaFijacion?: string;
  fechaDesfijacion?: string;
  constanciaKey?: string;
}

export interface CambiarEstadoInput {
  estado: EstadoNotificacion;
  fechaNotificacion?: string;
}

export interface ConstanciaInput {
  constanciaKey?: string;
  fechaNotificacion?: string;
}

// ---- Llamadas -------------------------------------------------------------

/** Central de notificaciones de la Secretaría (listado paginado con filtros). */
export function centralNotificaciones(params: {
  estado?: string; tipo?: string; page?: number; limit?: number;
} = {}): Promise<PaginaNotif<Notificacion>> {
  const qs = new URLSearchParams();
  if (params.estado) qs.set('estado', params.estado);
  if (params.tipo) qs.set('tipo', params.tipo);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return api<PaginaNotif<Notificacion>>(`/notificaciones/central${s ? `?${s}` : ''}`);
}

/** Notificaciones de un expediente. */
export function notificacionesExpediente(expedienteId: string): Promise<Notificacion[]> {
  return api<Notificacion[]>(`/expedientes/${expedienteId}/notificaciones`);
}

/** Crear una notificación en un expediente. */
export function crearNotificacion(
  expedienteId: string,
  body: CrearNotificacionInput,
): Promise<Notificacion> {
  return api<Notificacion>(`/expedientes/${expedienteId}/notificaciones`, { method: 'POST', body });
}

/** Cambiar el estado de una notificación. */
export function cambiarEstadoNotificacion(
  id: string,
  body: CambiarEstadoInput,
): Promise<Notificacion> {
  return api<Notificacion>(`/notificaciones/${id}/estado`, { method: 'PATCH', body });
}

/** Registrar la constancia de una notificación. */
export function registrarConstancia(
  id: string,
  body: ConstanciaInput,
): Promise<Notificacion> {
  return api<Notificacion>(`/notificaciones/${id}/constancia`, { method: 'POST', body });
}

// ---- RBAC -----------------------------------------------------------------

interface UsuarioSesion {
  id?: string;
  nombre?: string;
  rol?: string;
  [k: string]: unknown;
}

/** Lee el usuario en sesión desde localStorage ('usuario'). */
export function usuarioActual(): UsuarioSesion | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}

// Roles que pueden gestionar notificaciones (Central + acciones).
export const ROLES_NOTIFICACIONES = [
  'SECRETARIA',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

/** ¿El rol dado puede gestionar notificaciones? */
export function puedeGestionarNotificaciones(rol?: string): boolean {
  return !!rol && ROLES_NOTIFICACIONES.includes(rol);
}
